import { useEffect, useRef, useState } from 'react'
import { ApiError } from '@/api/client'
import { requestRolloutPreview, type RolloutPreviewGroup, type RolloutPreviewInput, type RolloutPreviewResult, type WaveDossier, type WaveReadiness } from '@/api/waves'
import Card from '@/components/Card'
import { NAMENSREGEL_HINWEIS, safeOperatorText, vorschauFehlertext } from './operatorText'

type Klasse = NonNullable<RolloutPreviewGroup['class']> | ''
interface Entwurf {
  ref: string
  klasse: Klasse
  memberIndices: number[]
  kampagne: string
  anzeigengruppe: string
  budgetCent: string
  queryIndices: number[]
}

const leer = (ref: string): Entwurf => ({ ref, klasse: '', memberIndices: [], kampagne: '', anzeigengruppe: '', budgetCent: '', queryIndices: [] })
const feld = 'w-full rounded-md border border-[var(--border)] bg-[var(--background)] p-2 text-sm'

/** Nur A/B-Dossierzeilen dürfen manuell zur Vorschau ausgewählt werden. */
export function selectableQueries(dossier: WaveDossier | undefined) {
  return (dossier?.queries ?? []).filter((query) =>
    (query.tier === 'A' || query.tier === 'B') && Number.isSafeInteger(query.sourceCampaignId) && query.sourceCampaignId > 0)
}

export function missingFields(groups: Entwurf[], readiness: WaveReadiness | undefined, dossier: WaveDossier | undefined, nowMs = Date.now()): string[] {
  const missing: string[] = []
  if (!readiness || !Array.isArray(readiness.checks) || readiness.checks.length === 0) missing.push('Readiness fehlt oder ist unbekannt')
  else if (!readiness.previewReady || readiness.checks.some((check) => check.category === 'preview' && check.status !== 'pass')) missing.push('Readiness blockiert die Vorschau')
  if (!readiness?.scopeHash || !readiness.dossierHash || !dossier?.economics?.dossier_hash) missing.push('Scope oder Dossier-Bindung fehlt')
  if (readiness && dossier?.economics && (readiness.scopeHash !== dossier.economics.scope_hash || readiness.dossierHash !== dossier.economics.dossier_hash)) missing.push('Scope oder Dossier ist veraltet')
  if (!readiness?.checkedAt || !Number.isFinite(Date.parse(readiness.checkedAt))) missing.push('Readiness-Zeitpunkt unbekannt')
  if (dossier?.economics && !dossier.economics.usable) missing.push('Economics-Dossier fachlich blockiert')
  if (dossier?.economics && (!dossier.economics.expires_at || !Number.isFinite(Date.parse(dossier.economics.expires_at)))) missing.push('Dossier-Gültigkeit unbekannt')
  if (dossier?.economics?.expires_at && Date.parse(dossier.economics.expires_at) <= nowMs) missing.push('Dossier ist abgelaufen')
  if (!readiness?.members?.length) missing.push('Keine Mitglieder verfügbar')
  if (readiness?.members?.some((member) => !member.asin || !member.sellerSku)) missing.push('Mitgliedsdaten unvollständig')
  if (!selectableQueries(dossier).length) missing.push('Keine A/B-Queries im Dossier')
  if (!groups.length) missing.push('Mindestens eine Gruppe anlegen')
  for (const [index, group] of groups.entries()) {
    const label = `Gruppe ${index + 1}`
    if (!['ZUGPFERDE', 'WACHSTUMSPOOL', 'SAMMELBECKEN'].includes(group.klasse)) missing.push(`${label}: Klasse wählen`)
    if (!group.memberIndices.length || group.memberIndices.some((item) => !Number.isSafeInteger(item) || item < 0 || item >= (readiness?.members?.length ?? 0))) missing.push(`${label}: Mitglieder wählen`)
    if (!group.kampagne.trim()) missing.push(`${label}: Kampagnenname fehlt`)
    if (!group.anzeigengruppe.trim()) missing.push(`${label}: Anzeigengruppenname fehlt`)
    if (!/^[1-9]\d*$/.test(group.budgetCent) || !Number.isSafeInteger(Number(group.budgetCent))) missing.push(`${label}: Tagesbudget in ganzen Cent größer 0 eingeben`)
    if (!group.queryIndices.length || group.queryIndices.some((item) => !Number.isSafeInteger(item) || item < 0 || item >= selectableQueries(dossier).length)) missing.push(`${label}: A/B-Query wählen`)
  }
  return missing
}

function makeInput(groups: Entwurf[], readiness: WaveReadiness, dossier: WaveDossier): RolloutPreviewInput {
  const queries = selectableQueries(dossier)
  return {
    scopeHash: readiness.scopeHash,
    dossierHash: readiness.dossierHash!,
    groups: groups.map((group) => ({
      clientRef: group.ref,
      class: group.klasse as NonNullable<RolloutPreviewGroup['class']>,
      members: group.memberIndices.map((index) => ({ asin: readiness.members[index]!.asin, sku: readiness.members[index]!.sellerSku })),
      campaignName: group.kampagne.trim(),
      adGroupName: group.anzeigengruppe.trim(),
      dailyBudgetEur: Number(group.budgetCent) / 100,
      queries: group.queryIndices.map((index) => ({ sourceCampaignId: queries[index]!.sourceCampaignId, term: queries[index]!.term })),
    })),
  }
}

export function RolloutEditor({ waveId, readiness, dossier, previewRequest = requestRolloutPreview }: {
  waveId: number | null
  readiness?: WaveReadiness
  dossier?: WaveDossier
  previewRequest?: typeof requestRolloutPreview
}) {
  const [groups, setGroups] = useState<Entwurf[]>([leer('gruppe-1')])
  const [result, setResult] = useState<RolloutPreviewResult | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [nowMs, setNowMs] = useState(() => Date.now())
  const generation = useRef(0)
  const controller = useRef<AbortController | null>(null)
  const nextRef = useRef(2)
  const queries = selectableQueries(dossier)
  const missing = missingFields(groups, readiness, dossier, nowMs)

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 30000)
    return () => clearInterval(timer)
  }, [])

  function invalidate() {
    generation.current += 1
    controller.current?.abort()
    controller.current = null
    setResult(null)
    setMessage(null)
    setBusy(false)
  }

  useEffect(() => {
    invalidate()
    return () => { generation.current += 1; controller.current?.abort() }
  }, [waveId, readiness, dossier])

  useEffect(() => {
    // Indizes duerfen nach einem Scopewechsel keine anderen Produkte/Queries zuordnen.
    setGroups((old) => old.map((group) => ({ ...group, memberIndices: [], queryIndices: [] })))
  }, [waveId, readiness?.scopeHash, readiness?.dossierHash])

  function changeGroup(index: number, patch: Partial<Entwurf>) {
    invalidate()
    setGroups((old) => old.map((group, position) => position === index ? { ...group, ...patch } : group))
  }

  async function submit() {
    invalidate()
    if (waveId === null || !Number.isSafeInteger(waveId) || waveId <= 0 || !readiness || !dossier || missingFields(groups, readiness, dossier).length) {
      setMessage('Vorschau nicht gesendet: fehlende oder veraltete Angaben prüfen.')
      return
    }
    const input = makeInput(groups, readiness, dossier)
    const requestId = generation.current
    const abort = new AbortController()
    controller.current = abort
    setBusy(true)
    try {
      const answer = await previewRequest(waveId, input, abort.signal)
      if (generation.current !== requestId || abort.signal.aborted) return
      if (answer.scopeHash !== input.scopeHash || answer.dossierHash !== input.dossierHash) {
        setMessage('Vorschau veraltet: Scope oder Dossier hat sich geändert. Bitte aktualisieren.')
      } else if (answer.blockers?.length || !answer.drafts?.length || answer.previewOnly !== true || answer.executable !== false) {
        setResult(answer)
        setMessage('Fachlicher Blocker: keine verwendbare Vorschau. Datenstand und Coverage prüfen.')
      } else {
        setResult(answer)
      }
    } catch (error) {
      if (generation.current !== requestId || abort.signal.aborted) return
      const fehler = error instanceof ApiError ? error : null
      setMessage(vorschauFehlertext(fehler?.status ?? null, fehler?.message, fehler?.body?.error?.[0]?.field))
    } finally {
      if (generation.current === requestId) { setBusy(false); controller.current = null }
    }
  }

  return (
    <Card className="space-y-4 p-4" data-testid="rollout-editor">
      <div><h2 className="text-sm font-semibold">S2/S3-Rollout-Vorschau</h2><p className="text-xs">Nur Berechnung. Klassen, Namen, Budget und Queries werden ausschließlich manuell gewählt.</p></div>
      {groups.map((group, index) => <fieldset key={group.ref} className="space-y-2 rounded-md border border-[var(--border)] p-3">
        <legend className="text-sm font-semibold">Gruppe {index + 1}</legend>
        <label className="block text-xs">Klasse
          <select aria-label={`Klasse Gruppe ${index + 1}`} className={feld} value={group.klasse} onChange={(event) => changeGroup(index, { klasse: event.target.value as Klasse })}>
            <option value="">Bitte wählen</option><option value="ZUGPFERDE">Zugpferde</option><option value="WACHSTUMSPOOL">Wachstumspool</option><option value="SAMMELBECKEN">Sammelbecken</option>
          </select>
        </label>
        <div className="text-xs">Mitglieder
          {(readiness?.members ?? []).map((member, memberIndex) => <label key={`${member.asin}-${member.sellerSku}-${memberIndex}`} className="block">
            <input type="checkbox" checked={group.memberIndices.includes(memberIndex)} onChange={() => changeGroup(index, { memberIndices: group.memberIndices.includes(memberIndex) ? group.memberIndices.filter((item) => item !== memberIndex) : [...group.memberIndices, memberIndex] })} /> {member.asin} · {member.sellerSku}
          </label>)}
        </div>
        <label className="block text-xs">Kampagnenname<input aria-label={`Kampagnenname Gruppe ${index + 1}`} className={feld} value={group.kampagne} onChange={(event) => changeGroup(index, { kampagne: event.target.value })} /></label>
        <label className="block text-xs">Anzeigengruppenname<input aria-label={`Anzeigengruppenname Gruppe ${index + 1}`} className={feld} value={group.anzeigengruppe} onChange={(event) => changeGroup(index, { anzeigengruppe: event.target.value })} /></label>
        <p data-testid="namensregel" className="text-xs">{NAMENSREGEL_HINWEIS} Der Kampagnenname muss im Profil eindeutig sein; der Anzeigengruppenname darf dem Kampagnennamen entsprechen.</p>
        <label className="block text-xs">Tagesbudget (Cent)<input aria-label={`Tagesbudget Gruppe ${index + 1}`} inputMode="numeric" className={feld} value={group.budgetCent} onChange={(event) => changeGroup(index, { budgetCent: event.target.value })} /></label>
        <div className="text-xs">A/B-Queries aus dem Dossier
          {queries.map((query, queryIndex) => <label key={`${query.sourceCampaignId}-${query.term}-${queryIndex}`} className="block">
            <input type="checkbox" checked={group.queryIndices.includes(queryIndex)} onChange={() => changeGroup(index, { queryIndices: group.queryIndices.includes(queryIndex) ? group.queryIndices.filter((item) => item !== queryIndex) : [...group.queryIndices, queryIndex] })} /> {query.term} · Tier {query.tier}
          </label>)}
        </div>
        <button type="button" onClick={() => { invalidate(); setGroups((old) => old.filter((_, position) => position !== index)) }}>Gruppe entfernen</button>
      </fieldset>)}
      <button type="button" onClick={() => { invalidate(); setGroups((old) => [...old, leer(`gruppe-${nextRef.current++}`)]) }}>Gruppe hinzufügen</button>
      {missing.length > 0 && <div data-testid="preview-missing"><p className="text-sm font-semibold">Für die Vorschau fehlen:</p><ul className="list-disc pl-5 text-xs">{missing.map((item) => <li key={item}>{item}</li>)}</ul></div>}
      <button type="button" disabled={busy || missing.length > 0} onClick={submit} className="rounded-md border border-[var(--border)] px-3 py-2 text-sm">{busy ? 'Vorschau wird berechnet …' : 'Vorschau berechnen'}</button>
      {message && <p role="alert">{message}</p>}
      {result?.blockers?.length ? <div data-testid="preview-blockers"><h3>Fachliche Blocker</h3><ul>{result.blockers.map((blocker, index) => <li key={index}>{safeOperatorText(blocker.reasonCode)}: {safeOperatorText(blocker.nextAction)}</li>)}</ul></div> : null}
      {result && !message && missing.length === 0 && <div data-testid="preview-result" className="space-y-2 text-sm">
        <p>Lokale oder serverseitige Berechnung: ausschließlich Vorschau, keine Freigabe.</p>
        {result.drafts.map((draft) => <div key={draft.clientRef} className="rounded-md border border-[var(--border)] p-2">
          <p>S2 · Kampagne: {draft.campaign.name} · {draft.class} · Tagesbudget {draft.campaign.dailyBudgetEur.toFixed(2)} € · pausiert</p>
          <p>S3 · Anzeigengruppe: {draft.adGroup.name} · pausiert</p>
          <ul>{draft.keywords.map((keyword) => <li key={keyword.clientRef}>{keyword.term} · Tier {keyword.tier} · Gebot {keyword.bidEur.toFixed(2)} € · Margen-Cap {keyword.economics.marginCapEur === null ? 'unbekannt' : `${keyword.economics.marginCapEur.toFixed(2)} €`} · Sellerboard-AOV-Vergleich (optional) {keyword.economics.legacyAovCapEur === null ? 'unbekannt' : `${keyword.economics.legacyAovCapEur.toFixed(2)} €`}</li>)}</ul>
        </div>)}
      </div>}
    </Card>
  )
}
